import { useEffect, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowRight, CalendarDays, Clock3, Tag } from 'lucide-react';
import { BLOG_POSTS, BLOG_POSTS_BY_SLUG } from '../content/blogPosts';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';

function formatDate(value) {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
}

function upsertMeta(name, content, isProperty = false) {
  const key = isProperty ? 'property' : 'name';
  let element = document.head.querySelector(`meta[${key}="${name}"]`);
  if (!element) {
    element = document.createElement('meta');
    element.setAttribute(key, name);
    document.head.appendChild(element);
  }
  element.setAttribute('content', content);
}

function upsertCanonical(href) {
  let element = document.head.querySelector('link[rel="canonical"]');
  if (!element) {
    element = document.createElement('link');
    element.setAttribute('rel', 'canonical');
    document.head.appendChild(element);
  }
  element.setAttribute('href', href);
}

function parseInline(text) {
  const out = [];
  const re = /(\*\*[^*]+\*\*|\[[^\]]+\]\([^)]+\))/g;
  let last = 0;
  let m;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) out.push(text.slice(last, m.index));
    const token = m[0];
    if (token.startsWith('**') && token.endsWith('**')) {
      out.push(<strong key={`${m.index}-b`}>{token.slice(2, -2)}</strong>);
    } else {
      const parts = token.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
      if (parts) {
        const href = parts[2];
        out.push(
          <a
            key={`${m.index}-a`}
            href={href}
            className="text-teal-700 hover:text-teal-800 underline underline-offset-4"
            target={href.startsWith('http') ? '_blank' : undefined}
            rel={href.startsWith('http') ? 'noreferrer' : undefined}
          >
            {parts[1]}
          </a>
        );
      } else {
        out.push(token);
      }
    }
    last = re.lastIndex;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

function MarkdownArticle({ markdown }) {
  const lines = markdown.split('\n');
  const nodes = [];
  let list = [];
  let key = 0;

  const flushList = () => {
    if (!list.length) return;
    nodes.push(
      <ul key={`ul-${key++}`} className="list-disc pl-6 space-y-2 text-slate-700 leading-relaxed">
        {list.map((item, idx) => (
          <li key={idx}>{parseInline(item)}</li>
        ))}
      </ul>
    );
    list = [];
  };

  for (const lineRaw of lines) {
    const line = lineRaw.trimEnd();
    if (!line.trim()) {
      flushList();
      continue;
    }
    if (line.startsWith('- ')) {
      list.push(line.slice(2));
      continue;
    }
    flushList();
    if (line.startsWith('### ')) {
      nodes.push(
        <h3 key={`h3-${key++}`} className="text-xl font-bold text-slate-900 mt-8 mb-3">
          {parseInline(line.slice(4))}
        </h3>
      );
      continue;
    }
    if (line.startsWith('## ')) {
      nodes.push(
        <h2 key={`h2-${key++}`} className="text-2xl font-bold text-slate-900 mt-10 mb-4">
          {parseInline(line.slice(3))}
        </h2>
      );
      continue;
    }
    if (line.startsWith('# ')) {
      nodes.push(
        <h1 key={`h1-${key++}`} className="text-3xl sm:text-4xl font-bold text-slate-900 mt-2 mb-4">
          {parseInline(line.slice(2))}
        </h1>
      );
      continue;
    }
    if (line.startsWith('> ')) {
      nodes.push(
        <blockquote key={`q-${key++}`} className="border-l-4 border-teal-300 pl-4 italic text-slate-700 my-4">
          {parseInline(line.slice(2))}
        </blockquote>
      );
      continue;
    }
    if (line === '---') {
      nodes.push(<hr key={`hr-${key++}`} className="my-8 border-slate-200" />);
      continue;
    }
    nodes.push(
      <p key={`p-${key++}`} className="text-slate-700 leading-relaxed my-3">
        {parseInline(line)}
      </p>
    );
  }
  flushList();
  return <div>{nodes}</div>;
}

export function BlogIndexPage() {
  const posts = useMemo(
    () => [...BLOG_POSTS].sort((a, b) => new Date(b.publishDate).getTime() - new Date(a.publishDate).getTime()),
    []
  );

  useEffect(() => {
    const canonical = `${window.location.origin}/blog`;
    document.title = 'Desqly Blog | AI Voice, Growth & Operations for Tradies';
    upsertMeta(
      'description',
      'Insights for tradies and service businesses on missed calls, AI voice agents, operations, customer sentiment, and growth.'
    );
    upsertMeta('og:title', 'Desqly Blog', true);
    upsertMeta(
      'og:description',
      'Practical guides on turning calls into booked jobs with AI voice and better operations.',
      true
    );
    upsertMeta('og:url', canonical, true);
    upsertMeta('twitter:title', 'Desqly Blog');
    upsertMeta('twitter:description', 'Practical AI voice and growth playbooks for tradies.');
    upsertCanonical(canonical);
  }, []);

  return (
    <div className="min-h-screen bg-slate-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-6xl mx-auto">
        <div className="text-center max-w-3xl mx-auto mb-12">
          <Badge className="bg-teal-100 text-teal-700 hover:bg-teal-100 mb-4">Desqly Blog</Badge>
          <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-4">Grow faster with better call handling</h1>
          <p className="text-slate-600 text-lg">
            Actionable articles for tradies and service operators on AI voice, lead conversion, customer experience, and
            operational efficiency.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {posts.map((post) => (
            <Card key={post.slug} className="border border-slate-200 hover:shadow-lg transition-all">
              <CardContent className="p-6">
                <div className="flex items-center gap-2 mb-3 text-xs text-slate-500">
                  <span className="inline-flex items-center gap-1">
                    <Tag className="w-3.5 h-3.5" />
                    {post.category}
                  </span>
                  <span>•</span>
                  <span className="inline-flex items-center gap-1">
                    <CalendarDays className="w-3.5 h-3.5" />
                    {formatDate(post.publishDate)}
                  </span>
                  <span>•</span>
                  <span className="inline-flex items-center gap-1">
                    <Clock3 className="w-3.5 h-3.5" />
                    {post.readingTime}
                  </span>
                </div>
                <h2 className="text-2xl font-bold text-slate-900 mb-3 leading-tight">{post.title}</h2>
                <p className="text-slate-600 mb-5">{post.excerpt}</p>
                <Button asChild className="bg-teal-600 hover:bg-teal-700">
                  <Link to={`/blog/${post.slug}`}>
                    Read article
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Link>
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
}

export function BlogDetailPage() {
  const { slug } = useParams();
  const post = slug ? BLOG_POSTS_BY_SLUG[slug] : null;

  useEffect(() => {
    if (!post) {
      document.title = 'Article not found | Desqly Blog';
      return;
    }
    const canonical = `${window.location.origin}/blog/${post.slug}`;
    document.title = `${post.title} | Desqly Blog`;
    upsertMeta('description', post.metaDescription);
    upsertMeta('keywords', [post.targetKeyword, ...(post.secondaryKeywords || [])].join(', '));
    upsertMeta('og:title', post.title, true);
    upsertMeta('og:description', post.metaDescription, true);
    upsertMeta('og:url', canonical, true);
    upsertMeta('twitter:title', post.title);
    upsertMeta('twitter:description', post.metaDescription);
    upsertCanonical(canonical);

    const existing = document.getElementById('blog-jsonld');
    if (existing) existing.remove();
    const script = document.createElement('script');
    script.id = 'blog-jsonld';
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'BlogPosting',
      headline: post.title,
      description: post.metaDescription,
      datePublished: post.publishDate,
      author: { '@type': 'Organization', name: post.author || 'Desqly' },
      publisher: { '@type': 'Organization', name: 'Desqly' },
      mainEntityOfPage: canonical,
      articleSection: post.category,
      keywords: [post.targetKeyword, ...(post.secondaryKeywords || [])].join(', '),
    });
    document.head.appendChild(script);
    return () => {
      const el = document.getElementById('blog-jsonld');
      if (el) el.remove();
    };
  }, [post]);

  if (!post) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <Card className="max-w-lg w-full">
          <CardContent className="p-8 text-center space-y-4">
            <h1 className="text-2xl font-bold text-slate-900">Article not found</h1>
            <p className="text-slate-600">This blog post may have moved or the URL is incorrect.</p>
            <Button asChild className="bg-teal-600 hover:bg-teal-700">
              <Link to="/blog">Back to blog</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-10 px-4 sm:px-6 lg:px-8">
      <article className="max-w-3xl mx-auto bg-white border border-slate-200 rounded-2xl shadow-sm p-6 sm:p-10">
        <div className="mb-8">
          <Link to="/blog" className="text-sm text-teal-700 hover:text-teal-800 underline underline-offset-4">
            Back to blog
          </Link>
          <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500 mt-4">
            <Badge className="bg-slate-100 text-slate-700 hover:bg-slate-100">{post.category}</Badge>
            <span>{formatDate(post.publishDate)}</span>
            <span>•</span>
            <span>{post.readingTime} read</span>
            <span>•</span>
            <span>By {post.author}</span>
          </div>
        </div>

        <MarkdownArticle markdown={post.content} />

        <div className="mt-10 pt-6 border-t border-slate-200">
          <p className="text-sm text-slate-600 mb-3">
            Ready to capture more calls and turn more enquiries into booked work?
          </p>
          <Button asChild className="bg-teal-600 hover:bg-teal-700">
            <Link to="/?lead=1">
              Get started with Desqly
              <ArrowRight className="w-4 h-4 ml-2" />
            </Link>
          </Button>
        </div>
      </article>
    </div>
  );
}

